/**
 * „Direkte Bestell-Links: was 1.000 Kontakte bringen" (P3-Review, vorschlag_v3 §2.5; Till E2-A). Block B der
 * Werbeflächen-Antwort: Mail oder Anzeige führt direkt auf eine Bestellseite, gezählt werden nur Käufe über genau
 * diesen Link (Kennung im Bestell-Link). Je Fläche Kontakte, erwarteter Wert je 1.000 (Sortierung), Abmeldungen
 * je 1.000 und der erwartete Wert als Summe — die Summe zeigt den blinden Fleck der Flächen ohne Kontaktzahl.
 * Spaltenwahl je Kartenbreite (vorschlag_v3 §5), Speicherschlüssel `bestelllinks`.
 *
 * Props: `data` = Antwort von /api/auswertungen/kanaele/werbeflaechen in der neuen Form (`helpers/auswertungenData.ts`,
 * `fetchWerbeflaechen`), `null` solange sie lädt; `fehler` = Ladefehler oder `null`. Dieselbe Antwort wie
 * die Einladungs-Karte (E2-A) — daraus kommt auch der Vergleichswert der laufenden Heat-Ups im Kartenkopf.
 */
import { FC, ReactNode, useMemo } from 'react';
import { Box } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { ATTRIBUTION, TERMS } from '../../../helpers/kpiDefinitions';
import { Hinweis, Laden } from '../webinare-produkte/shared';
import {
  anfosText,
  anz,
  datumLang,
  euro1,
  FZeile,
  grauAnfos,
  kartenkopf,
  KontakteZelle,
  MitUnterzeile,
  NameZelle,
  Strich,
  summeEuro,
  vorlaeufigHinweis,
  zeilenAus,
  zeilenInfo,
} from './flaechenTeile';
import { euroJeTausend, jeTausend, kompakt, useBreitePx } from './shared';
import type { FlaechenZeile, FlaechenGrauSchwellen, WerbeflaechenAntwort } from '../../../helpers/auswertungenData';

const SAMMEL_GRUND = 'Ohne Kontaktzahl keine Werte je 1.000 — die Flächen stehen beim Aufklappen in den Zeilen darunter.';
const WARM = 'Nur an Webinar-Angemeldete verschickt — nicht mit Mails an die ganze Liste vergleichen';

/** Menügruppen in fester Reihenfolge (vorschlag_v3 §5.3). */
const GRUPPEN = ['Menge', 'Wert', 'Abmeldungen'] as const;

/** Standard je Kartenbreite (vorschlag_v3 §5.2): 5 Spalten ab 640 px, Handy Werbefläche | Erwarteter Wert | Abmeldungen. */
const BREIT = ['name', 'kontakte', 'wert1k', 'abm1k', 'wertSumme'];
export const BESTELLLINKS_STANDARDS: Record<Breite, readonly string[]> = {
  breit: BREIT,
  mittel: BREIT,
  schmal: ['name', 'wert1k', 'abm1k'],
};

const MENUE: Record<string, string> = {
  kontakte: TERMS.kontakteZustellungen.label,
  versaende: TERMS.versaende.label,
  wert1k: TERMS.wertJe1kFlaeche.label,
  wertSumme: TERMS.wertSumme.label,
  anfos1k: TERMS.anfosJe1kFlaeche.label,
  abm1k: TERMS.abmeldungenJe1k.label,
  wertJeAbm: TERMS.wertJeAbmeldung.label,
  abmAnzahl: `${TERMS.abmeldungenAnzahl.label} (Anzahl)`,
};

const SCHWELLEN_ERSATZ: FlaechenGrauSchwellen = { anfos: 20, anmeldungen: 50, rechtzeitig: 50, abmeldungen: 20, nach_beginn_anmeldungen: 50 };

interface Kontext {
  data: WerbeflaechenAntwort | null;
  sw: FlaechenGrauSchwellen;
  sichtbar: ReadonlySet<string>;
  schmal: boolean;
}

function baueSpalten(c: Kontext): Spalte<FZeile>[] {
  const { sw, data } = c;
  const wenig = TERMS.wenigDaten.etikett;
  const nurMitWert = (v: number | null | undefined, grund: string | null) => (v === null || v === undefined ? null : grund);
  /** Zelle einer Fläche; die Sammelzeile zeigt `sammel` (Standard „—" mit Grund). */
  const zelle = (f: (z: FlaechenZeile) => ReactNode, sammel?: (r: Extract<FZeile, { art: 'sammel' }>) => ReactNode) =>
    function zellInhalt(r: FZeile): ReactNode {
      if (r.art === 'sammel') return sammel ? sammel(r) : <Strich grund={SAMMEL_GRUND} />;
      return f(r.z);
    };
  const nurFlaeche =
    <V,>(f: (z: FlaechenZeile) => V) =>
    (r: FZeile): V | null =>
      r.art === 'sammel' ? null : f(r.z);
  const unsicherFlaeche = (f: (z: FlaechenZeile) => string | null) => (r: FZeile) => (r.art === 'sammel' ? null : f(r.z));

  return [
    {
      key: 'name',
      // Am Handy „Fläche“: Kopf „Werbefläche“+ⓘ hielt die Spalte sonst 97 px breit (3 px Überlauf bei 390 px, gemessen 23.10.).
      label: c.schmal ? TERMS.werbeflaeche.short : TERMS.werbeflaeche.label,
      tip: TERMS.werbeflaeche.tip,
      gruppe: 'Menge',
      pflicht: true,
      minWidth: c.schmal ? 72 : 220,
      render: (r, k) => {
        if (!data) return null;
        if (r.art === 'sammel') {
          return (
            // Name inline (der Aufklapp-Knopf der Tabelle steht davor in derselben Zeile), die Stückzahlen darunter.
            <Box component="span" sx={{ lineHeight: 1.3, fontWeight: 600 }}>
              Ohne Kontaktzahl ({anz(r.flaechen)} {r.flaechen === 1 ? 'Fläche' : 'Flächen'})
              <InfoTip text="Flächen ohne Empfängerzahl: Ihre Anfos und ihr erwarteter Wert zählen, Werte je 1.000 Kontakte gibt es nicht. Die Summe rechts zeigt, wie viel Wert ohne Kontaktzahl entsteht." />
              <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
                {anfosText(r.anfos)}
              </Box>
            </Box>
          );
        }
        const z = r.z;
        const kontakteVersteckt = !c.sichtbar.has('kontakte') && z.kontakte !== null;
        const info = zeilenInfo(z, [
          kontakteVersteckt && `${anz(z.kontakte)} Kontakte (zugestellte Mails) in ${anz(z.versaende)} ${z.versaende === 1 ? 'Versand' : 'Versänden'}.`,
          z.provisional > 0 && `Dazu ${anz(z.provisional)} vorläufig ergänzte Anfos, die in keiner Quote zählen.`,
        ]);
        const unter = z.kontakte === null ? anfosText(z.anfos_ohne_vorlaeufige) : undefined;
        return <NameZelle z={z} data={data} info={info} sortGrund={k.ebene === 0 ? k.sortGrund : null} unter={unter} />;
      },
    },
    {
      key: 'kontakte',
      label: TERMS.kontakteZustellungen.label,
      tip: TERMS.kontakteZustellungen.tip,
      hinweis: 'Darunter die Zahl der Versände; der genaue Wert steht im Tooltip der Zahl. Beim Nachfass sind es Angemeldete.',
      einheit: 'zugestellte Mails',
      align: 'right',
      gruppe: 'Menge',
      render: zelle((z) => <KontakteZelle z={z} mitVersaenden={!c.sichtbar.has('versaende')} />),
      sortValue: nurFlaeche((z) => z.kontakte),
    },
    {
      key: 'versaende',
      label: TERMS.versaende.label,
      tip: TERMS.versaende.tip,
      einheit: 'Anzahl',
      align: 'right',
      gruppe: 'Menge',
      render: zelle((z) => anz(z.versaende)),
      sortValue: nurFlaeche((z) => z.versaende),
    },
    {
      key: 'wert1k',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertJe1kFlaeche.tip,
      hinweis: `Darunter die Anfos (ohne vorläufig ergänzte). Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Wert',
      pflicht: true,
      render: zelle((z) => {
        if (z.wert_je_1000 === null) {
          return (
            <Strich grund={z.ohne_nenner_grund ?? (z.anfos_ohne_wert > 0 ? 'Keine Anfo dieser Fläche hat einen erwarteten Wert.' : 'Nicht messbar.')} />
          );
        }
        const tip = [
          z.anfos_ohne_wert > 0 ? `${anz(z.anfos_ohne_wert)} Anfos ohne Wert zählen 0 €.` : '',
          z.provisional > 0 ? `+${anz(z.provisional)} vorläufig ergänzt (nicht im Wert).` : '',
        ]
          .filter(Boolean)
          .join(' ');
        return (
          <MitUnterzeile
            fett={!z.warm}
            wert={
              <>
                {euroJeTausend(z.wert_je_1000)}
                {tip && <InfoTip text={tip} />}
              </>
            }
            unter={anfosText(z.anfos_ohne_vorlaeufige)}
          />
        );
      }),
      sortValue: nurFlaeche((z) => z.wert_je_1000),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.wert_je_1000, grauAnfos(z.anfos_ohne_vorlaeufige, sw, wenig.anfos))),
    },
    {
      key: 'anfos1k',
      label: TERMS.anfos.label,
      tip: TERMS.anfosJe1kFlaeche.tip,
      hinweis: `Darunter die Anfos (ohne vorläufig ergänzte). Grau unter ${anz(sw.anfos)} Anfos.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Wert',
      render: zelle((z) =>
        z.anfos_je_1000 === null ? (
          <Strich grund={z.ohne_nenner_grund ?? 'Nicht messbar.'} />
        ) : (
          <MitUnterzeile wert={jeTausend(z.anfos_je_1000)} unter={anfosText(z.anfos_ohne_vorlaeufige)} />
        ),
      ),
      sortValue: nurFlaeche((z) => z.anfos_je_1000),
      unsicher: unsicherFlaeche((z) => nurMitWert(z.anfos_je_1000, grauAnfos(z.anfos_ohne_vorlaeufige, sw, wenig.anfos))),
    },
    {
      key: 'abm1k',
      // Am Handy die Kurzform (Menü und Tooltip tragen den vollen Begriff): sonst 16 px Überlauf bei 390 px (gemessen 23.10.).
      label: c.schmal ? TERMS.abmeldungenAnzahl.short : TERMS.abmeldungenAnzahl.label,
      tip: TERMS.abmeldungenJe1k.tip,
      hinweis: `Grau unter ${anz(sw.abmeldungen)} Abmeldungen.`,
      einheit: 'je 1.000 Kontakte',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle((z) =>
        z.abmeldungen_je_1000 === null ? (
          <Strich grund={z.abmeldungen_grund ?? z.ohne_nenner_grund ?? 'Nicht messbar.'} />
        ) : (
          jeTausend(z.abmeldungen_je_1000)
        ),
      ),
      sortValue: nurFlaeche((z) => z.abmeldungen_je_1000),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(z.abmeldungen_je_1000, z.abmeldungen !== null && z.abmeldungen < sw.abmeldungen ? wenig.abmeldungen : null),
      ),
    },
    {
      key: 'abmAnzahl',
      label: TERMS.abmeldungenAnzahl.label,
      tip: TERMS.abmeldungenAnzahl.tip,
      einheit: 'Anzahl',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle((z) => (z.abmeldungen === null ? <Strich grund={z.abmeldungen_grund ?? 'Nicht messbar.'} /> : anz(z.abmeldungen))),
      sortValue: nurFlaeche((z) => z.abmeldungen),
    },
    {
      key: 'wertJeAbm',
      label: TERMS.wertJeAbmeldung.label,
      tip: TERMS.wertJeAbmeldung.tip,
      hinweis: `Grau unter ${anz(sw.anfos)} Anfos oder ${anz(sw.abmeldungen)} Abmeldungen.`,
      einheit: 'erwarteter Wert',
      align: 'right',
      gruppe: 'Abmeldungen',
      render: zelle((z) =>
        z.wert_je_abmeldung === null ? <Strich grund={z.abmeldungen_grund ?? 'Keine Abmeldungen über diese Fläche.'} /> : euro1(z.wert_je_abmeldung),
      ),
      sortValue: nurFlaeche((z) => z.wert_je_abmeldung),
      unsicher: unsicherFlaeche((z) =>
        nurMitWert(
          z.wert_je_abmeldung,
          z.anfos_ohne_vorlaeufige < sw.anfos ? wenig.anfos : z.abmeldungen !== null && z.abmeldungen < sw.abmeldungen ? wenig.abmeldungen : null,
        ),
      ),
    },
    {
      key: 'wertSumme',
      label: TERMS.wertBeiBestellung.short,
      tip: TERMS.wertSumme.tip,
      hinweis: 'Zeigt auch den Wert der Flächen ohne Kontaktzahl, die in den Spalten je 1.000 fehlen.',
      einheit: 'Summe',
      align: 'right',
      gruppe: 'Wert',
      render: zelle(
        (z) => summeEuro(z.wert_summe),
        (r) => summeEuro(r.wertSumme),
      ),
      sortValue: nurFlaeche((z) => z.wert_summe),
    },
  ];
}

const menueAus = (spalten: Spalte<FZeile>[]) =>
  spaltenMeta(spalten)
    .map((m) => ({ ...m, label: MENUE[m.key] ?? m.label }))
    .sort((a, b) => GRUPPEN.indexOf(a.gruppe as (typeof GRUPPEN)[number]) - GRUPPEN.indexOf(b.gruppe as (typeof GRUPPEN)[number]));

/** „19–20 €" aus den laufenden Heat-Ups (Vortag, Webinartag) derselben Antwort; null ohne Werte. */
function heatUpSpanne(data: WerbeflaechenAntwort): string | null {
  const werte = data.rows
    .filter((z) => (z.key === 'heatup_vortag' || z.key === 'heatup_webinartag') && !z.beendet_am)
    .map((z) => z.wert_je_1000)
    .filter((v): v is number => v !== null);
  if (werte.length === 0) return null;
  const min = euroJeTausend(Math.min(...werte));
  const max = euroJeTausend(Math.max(...werte));
  return min === max ? min : `${min.replace(/\s?€$/, '')}–${max}`;
}

const BestellLinksKarte: FC<{ data: WerbeflaechenAntwort | null; fehler: string | null }> = ({ data, fehler }) => {
  const block = useMemo(() => (data ? data.rows.filter((z) => z.block === 'bestellung') : []), [data]);
  const zeilen = useMemo(() => zeilenAus(block, (a, b) => b.anfos_ohne_vorlaeufige - a.anfos_ohne_vorlaeufige || b.wert_summe - a.wert_summe), [block]);
  const sw = data?.grau_schwellen ?? SCHWELLEN_ERSATZ;

  const meta = menueAus(baueSpalten({ data, sw, sichtbar: new Set(), schmal: false }));
  const wahl = useSpaltenwahl('bestelllinks', meta, BESTELLLINKS_STANDARDS);
  const [breiteRef, breitePx] = useBreitePx(wahl.messRef);
  const sichtbar = new Set(wahl.sichtbar);
  const schmal = wahl.breite === 'schmal';
  const spalten = baueSpalten({ data, sw, sichtbar, schmal }).filter((s) => sichtbar.has(s.key));
  const heatUps = data ? heatUpSpanne(data) : null;
  const dmOhne = data?.dm_ohne_vorlaeufige_artikel ?? [];

  return (
    <ChartCard
      title="Direkte Bestell-Links: was 1.000 Kontakte bringen"
      anker="direkte-bestell-links"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Was 1.000 Kontakte bringen, wenn Mail oder Anzeige direkt auf eine Bestellseite führen, und was die Fläche an Abmeldungen kostet.
          </Box>{' '}
          Gezählt werden nur Käufe über genau diesen Link. Die Webinar-Einladungen zählen weiter: Dort gehört jede erste Anfo einer angemeldeten
          Person zum Webinar zur Einladung, egal über welchen Link. In der Größenordnung sind beide Karten vergleichbar (gleicher Nenner:
          zugestellte Mails; gleiche Geldgröße), in Feinheiten nicht.
        </>
      }
      terms={[
        { label: ATTRIBUTION.bannerFlaeche.label, tip: ATTRIBUTION.bannerFlaeche.tip },
        { label: TERMS.werbeflaeche.label, tip: TERMS.werbeflaeche.tip },
        { label: TERMS.kontakteZustellungen.label, tip: TERMS.kontakteZustellungen.tip },
        { label: TERMS.wertJe1kFlaeche.label, tip: TERMS.wertJe1kFlaeche.tip },
        { label: TERMS.abmeldungenJe1k.label, tip: TERMS.abmeldungenJe1k.tip },
        { label: TERMS.wertSumme.label, tip: TERMS.wertSumme.tip },
        {
          label: TERMS.wenigDaten.label,
          tip: `${TERMS.wenigDaten.tip} Grau unter ${anz(sw.anfos)} Anfos (Wert und Anfos je 1.000) und unter ${anz(sw.abmeldungen)} Abmeldungen.`,
        },
        ...(block.some((z) => z.provisional > 0) ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="Direkt-Mailings, Newsletter-Ausgaben, Versand-Bücher der Flächen und PL-X-Bestellungen"
      freshnessKey={['newsletter_ausgaben', 'plx_orders', 'anfo_werte']}
    >
      <Box ref={breiteRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>{kartenkopf(data, block, true)}</Box>
            {heatUps && (
              <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
                Zum Vergleich: Die laufenden Heat-Ups bringen {heatUps} je 1.000 Kontakte (Karte darüber).
              </Box>
            )}
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(r) => r.id}
              initialSort={{ key: 'wert1k', dir: 'desc' }}
              kinder={(r) => (r.art === 'sammel' ? r.kinder : undefined)}
              abschnitt={(r) => (r.art === 'sammel' ? '' : r.z.warm ? WARM : r.z.beendet_am ? 'Nicht mehr im Einsatz' : null)}
              eng={schmal || (breitePx !== null && breitePx < 860)}
              festeErsteSpalte
            />
            <Hinweis>
              {/* B62 (26.10.2026): „zählen zum Versand“ galt nur für die Direkt-Mailings; die Banner-Flächen zählen nach Bestelltag. */}
              Euro netto. — = nicht messbar; der Grund steht im Tooltip. Direkt-Mailings und der Nachversand an Nicht-Öffner zählen die Anfos
              zum Versand (Mailing, Marke und Tag im Bestell-Link), die übrigen Flächen nach Bestelltag im Versandzeitraum; „Woher die Anfos
              kommen“ zählt alle nach Bestelltag.
              {dmOhne.length > 0 &&
                ` Die Direkt-Mailing-Zeilen rechnen ohne ${dmOhne.map((a) => `${a.name} (${anz(a.anfos)} ${a.anfos === 1 ? 'Anfo' : 'Anfos'}, alle vorläufig; ${kompakt(a.kontakte)} Kontakte)`).join(', ')} — bei Kontakten, Anfos und Abmeldungen, wie „Webinare & Produkte“.`}{' '}
              Für eine Grundsatzfrage wie „Direkt-Mailing oder Heat-Up“ taugt der Vergleich mit der Karte darüber,
              für Abstufungen von wenigen Euro nicht. Newsletter-Plätze haben Ausgabendaten erst ab {datumLang(data.newsletter_daten_ab)}.
              {vorlaeufigHinweis(block)}
              {data.stichtag_hinweis ? ` ${data.stichtag_hinweis}` : ''}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default BestellLinksKarte;
